/**
 * Publicar (docs/refactor/33-publicar.md): the project, brought up by ▶ Executar's sibling prompt
 * ("run.publish"), shared at a public address so the person can try it away from home.
 *
 * The agent only says what it started, one line per service:
 *   DEVPULSE-SERVICE: <name> | <local url> | <front|api|other> | <main: yes|no>
 * The desktop app (never the agent) opens a tunnel per service (src-tauri/src/tunnels.rs, which refuses
 * databases, the Docker API and anything not answering HTTP on this computer) and adds what it opened
 * to the reply, so the web gets it from the work order like any other answer:
 *   DEVPULSE-PUBLISHED: <name> | <public url> | <role> | <main: yes|no>
 *   DEVPULSE-NOT-PUBLISHED: <name> | <reason>
 */

/** Sent on this window when the app has just shared something, so the run bar shows the links at once. */
export const PUBLISHED_EVENT = 'devpulse:published'

/** Put at the top of the publish prompt: what tells the runner that this run is the person asking to publish. */
export const PUBLISH_MARKER = '[vephus:publish]'

export type ServiceRole = 'front' | 'api' | 'other'

export interface LocalService {
  name: string
  url: string
  port: number
  role: ServiceRole
  main: boolean
}

export interface PublishedService {
  name: string
  url: string
  role: ServiceRole
  main: boolean
}

export interface PublishOutcome {
  published: PublishedService[]
  refused: Array<{ name: string; reason: string }>
}

const roleOf = (text: string | undefined): ServiceRole => {
  const role = (text ?? '').trim().toLowerCase()
  return role === 'front' || role === 'frontend' || role === 'web' ? 'front' : role === 'api' || role === 'backend' ? 'api' : 'other'
}

// "yes", or "main: yes" as agents often copy the template.
const yes = (text: string | undefined) => /^(?:(?:main|principal)\s*:\s*)?(yes|sim|true)$/i.test((text ?? '').trim())

/**
 * "Parar de compartilhar" sent from the web: a queued item the computer at home recognises and answers by closing its
 * links, without starting an agent. It only ever stops things, so whoever may queue work for this person may send it.
 */
export const UNPUBLISH_MARKER = '[vephus:unpublish]'

export function isUnpublishRequest(text: string): boolean {
  return text.trim() === UNPUBLISH_MARKER
}

/** Whether this run is the person's "Publicar", by the marker the catalog prompt starts with. */
export function isPublishRequest(prompt: string): boolean {
  return prompt.includes(PUBLISH_MARKER)
}

/** The services the agent reported, local addresses only, one per port, with exactly one marked main. */
export function servicesIn(output: string): LocalService[] {
  const byPort = new Map<number, LocalService>()
  for (const match of output.matchAll(/DEVPULSE-SERVICE:\s*([^\n]+)/g)) {
    const [name, address, role, main] = match[1]!.split('|').map((part) => part.trim().replace(/^[`'"]+|[`'".,;]+$/g, ''))
    if (!name || !address) continue
    let url: URL
    try {
      url = new URL(address)
    } catch {
      continue
    }
    const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]'
    if (!local || url.protocol !== 'http:') continue
    const port = Number(url.port || 80)
    if (!byPort.has(port)) byPort.set(port, { name: name.slice(0, 60), url: url.toString(), port, role: roleOf(role), main: yes(main) })
  }
  const services = [...byPort.values()]
  return withOneMain(services)
}

/** The one to open: the one the agent marked; else the front; else the only API; else the first. */
function withOneMain<T extends { role: ServiceRole; main: boolean }>(services: T[]): T[] {
  const chosen = services.find((s) => s.main) ?? services.find((s) => s.role === 'front') ?? services.find((s) => s.role === 'api') ?? services[0]
  return services.map((s) => ({ ...s, main: s === chosen }))
}

/** The lines the runner adds to the reply, for the web and for the person reading it. */
export function publishedLines(outcome: PublishOutcome): string {
  return [
    ...outcome.published.map((s) => `DEVPULSE-PUBLISHED: ${s.name} | ${s.url} | ${s.role} | ${s.main ? 'yes' : 'no'}`),
    ...outcome.refused.map((s) => `DEVPULSE-NOT-PUBLISHED: ${s.name} | ${s.reason}`),
  ].join('\n')
}

/** What a finished publish run opened, read back from its reply; the main one first. */
export function publishedIn(text: string): PublishOutcome {
  const published: PublishedService[] = []
  for (const match of text.matchAll(/DEVPULSE-PUBLISHED:\s*([^\n]+)/g)) {
    const [name, url, role, main] = match[1]!.split('|').map((part) => part.trim())
    if (!name || !url || !/^https:\/\/[a-z0-9-]+\.trycloudflare\.com\/?$/i.test(url)) continue
    published.push({ name, url, role: roleOf(role), main: yes(main) })
  }
  const refused: PublishOutcome['refused'] = []
  for (const match of text.matchAll(/DEVPULSE-NOT-PUBLISHED:\s*([^\n]+)/g)) {
    const [name, reason] = match[1]!.split('|').map((part) => part.trim())
    if (name) refused.push({ name, reason: reason ?? '' })
  }
  return { published: [...published].sort((a, b) => Number(b.main) - Number(a.main)), refused }
}

/** The reasons tunnels.rs gives, in words a person can act on (keys under publish.refused.*). */
export function refusalKey(reason: string): string {
  if (reason.includes('port-not-allowed')) return 'publish.refused.port'
  if (reason.includes('not-answering')) return 'publish.refused.down'
  if (reason.includes('download')) return 'publish.refused.download'
  if (reason.includes('unsupported-system')) return 'publish.refused.system'
  return 'publish.refused.unknown'
}
